/**
 * English - Landing Page translations
 */
export const landing = {
  hero: {
    title: 'DR Mortgages & Refinancing — One Application, Every Bank.',
    subtitle: 'The mortgage gateway for the Dominican Republic (DR)',
    description: 'Whether you\'re buying property or unlocking equity in one you already own — HipoTech packages, translates, encrypts, and delivers your application to every qualifying bank simultaneously. One application, competing offers, real-time results.',
    cta: 'Start Your Application',
    secondaryCta: 'Learn How It Works',
    simulatorCta: 'Try the simulator — free',
  },
  features: {
    title: 'The Multi-Bank Mortgage Platform for the Dominican Republic',
    subtitle: 'One application. Every qualifying DR bank. Real-time results.',
    multiBank: {
      title: 'Multi-Bank Access',
      description: 'Apply to multiple banks with a single application',
    },
    guided: {
      title: 'Guided Process',
      description: 'Step-by-step guidance through the entire application',
    },
    secure: {
      title: 'Bank-Level Security',
      description: 'Your data is protected with enterprise-grade encryption',
      maskingLink: 'See how we protect your documents',
    },
    support: {
      title: 'Expert Support',
      description: 'Our team is here to help you every step of the way',
    },
    precon: {
      title: 'Pre-Construction Ready',
      description: 'Buy off-plan with confidence. Builder verification, construction tracking, CONFOTUR tax benefits, and automatic reprocessing when your property is delivered.',
    },
    equity: {
      title: 'Equity & Refinancing',
      description: 'Borrow against property you already own. Consolidate debts, fund improvements, or unlock equity — with multiple banks competing for your refinancing.',
    },
  },
  howItWorks: {
    title: 'How It Works',
    subtitle: 'From application to bank offers — here\'s exactly what happens',
    step1: {
      title: 'Complete Your Profile',
      description: 'Tell us about yourself, your finances, and the property you\'re buying or refinancing. Apply in English, Spanish, or French.',
    },
    step2: {
      title: 'Upload Documents',
      description: 'Securely upload proof of income, ID, and supporting documents. We verify and translate everything.',
    },
    step3: {
      title: 'Pay & Submit Securely',
      description: 'After a one-time processing fee, enter sensitive data on a secure form. Your account and routing numbers are processed in memory and destroyed within seconds — never stored.',
    },
    step4: {
      title: 'Banks Compete for You',
      description: 'Your application is matched, packaged, and delivered to every qualifying bank. Track delivery, access, and responses in real time on your dashboard.',
    },
  },
  cta: {
    title: 'Ready to Get Started?',
    subtitle: 'Use a smarter way to get a mortgage in the Dominican Republic (DR).',
    button: 'Start Your Application',
    appreciationNote: 'Did you know? DR property values have risen 7-11% per year — often outpacing mortgage interest costs.',
    appreciationLink: 'See the math →',
  },
  dueDiligence: {
    title: 'Due Diligence',
    description: 'Explore HipoTech from every stakeholder\'s perspective — buyers, sellers, banks, agents, regulators, appraisers, and IT security.',
    cta: 'Explore All Perspectives',
    nav: {
      buyers:     'Buyers',
      sellers:    'Sellers',
      banks:      'Banks',
      agents:     'Agents & Builders',
      regulators: 'Regulators',
      appraisers: 'Appraisers',
      it:         'IT & Security',
    },
  },
  invite: {
    sectionTitle: 'Not looking for a mortgage yourself?',
    sectionSubtitle: 'If you know someone who is — a friend, family member, or colleague — send them a quick personalized invite.',
    widgetTitle: 'Know someone this is for?',
    widgetSubtitle: 'Send them a quick invite — they\'ll get a personalized email in their language.',
    yourName: 'Your name',
    yourNameHint: 'Used to personalise the invitation email.',
    theirName: 'Their name',
    theirEmail: 'Their email address',
    sendInvite: 'Send invite',
    sending: 'Sending',
    sent: 'Invitation sent!',
    sentDetail: 'will receive a personalized email shortly.',
    yourContact: 'Your contact',
    inviteAnother: 'Invite another person',
    inviteMultiple: 'Invite multiple people →',
    invitingSeveral: 'Inviting several people?',
    fullInvitePage: 'Use the full invite page →',
    errorFallback: 'Could not send the invitation. Please try again.',
    pageTitle: 'Invite someone to HipoTech',
    pageSubtitle: 'Know someone looking to buy property in the Dominican Republic? Send them a personalized invitation — they\'ll receive an email in their preferred language.',
    whoAreYou: 'Who are you?',
    yourLanguage: 'Your language',
    whoToInvite: 'Who do you want to invite?',
    eachPersonReceives: 'Each person receives a personalized email in their own language.',
    addAnother: 'Add another invitee',
    sendCount: 'Send {count} invitation(s)',
    disclaimer: 'Invitations are sent immediately. Recipients can always unsubscribe.',
    nameEmailRequired: 'Name and email are required.',
    inviteeLabel: 'Invitee',
    previouslyInvited: 'previously invited',
    remove: 'Remove',
    fullName: 'Full name',
    emailAddress: 'Email address',
    reSend: 'Re-send invitation to',
    sendTo: 'Send invitation to',
    reSendOne: 'Re-send',
    invite: 'Invite',
    reInvite: 'Re-invite',
    invitationSentTo: 'Invitation sent to',
    failedToSend: 'Failed to send. Please try again.',
    previewTitle: 'Preview the email',
    previewSubtitle: 'See exactly what your invitee will receive',
    previewIn: 'Preview in',
    subject: 'Subject',
    emailBody: 'Email body',
    fillInNames: 'Fill in the names above to personalize this preview.',
    exactlyWhatRecipientSees: 'This is exactly what the recipient will see.',
    backToHome: 'Back to home',
  },
  stats: {
    responseTime: '7-14 Days',
    responseTimeLabel: 'Bank Response Time',
    applications: 'Applications Processed',
    banksCount: '10',
    banks: 'Partner Banks',
    approval: 'Pre-Qualification Rate',
    support: 'Trusted customers in',
  },
  banks: {
    trustedBy: 'Trusted by leading banks in the Dominican Republic',
  },
  security: {
    zeroPersistence: {
      title: 'Zero-Persistence Architecture',
      description: 'Sensitive data exists for only 10-30 seconds during processing',
    },
    encryptedVault: {
      title: 'Encrypted Document Vault',
      description: 'Only encrypted PDFs stored in separate secure infrastructure',
    },
    noSsn: {
      title: 'No Financial Data Storage',
      description: 'Bank account numbers never touch our database',
    },
    noAccountNumber: {
      title: 'No Account Number Storage',
      description: 'Bank account numbers destroyed after document generation',
    },
    referenceOnly: {
      title: 'Reference-Only Display',
      description: 'Only last 4 digits shown for your convenience',
    },
    soc2: {
      title: 'SOC 2 Compliance-Ready',
      description: 'Secure controls and processes',
    },
  },
  countries: {
    US: 'United States',
    CA: 'Canada',
    DO: 'Dominican Republic',
    ES: 'Spain',
    FR: 'France',
  },
  stepLabel: 'Step',
  gateway: {
    badge: 'GATEWAY, NOT A BROKER',
    title: 'Technology Platform, Not a Mortgage Broker',
    description: 'HipoTech is not a mortgage broker. We don\'t recommend banks or negotiate terms on your behalf. We\'re a secure technology platform that packages your application, translates it, encrypts it, and delivers it simultaneously to every bank where you qualify — then tracks the results. The decision is always between you and the bank.',
    point1title: 'No Conflicts of Interest',
    point1desc: 'We have no incentive to steer you toward any particular bank. Every qualifying bank gets your application equally — no commissions, no steering, no kickbacks.',
    point2title: 'You Choose',
    point2desc: 'When banks respond with offers, you compare and decide. We provide the information — you make the call.',
    point3title: 'Transparent Process',
    point3desc: 'Track exactly where your application is at every step: delivered, opened, downloaded, reviewed, and responded to.',
    point4title: 'Same Documents Either Way',
    point4desc: 'A broker does not reduce what you need to gather. You still provide the same income statements, bank records, and ID — the difference is that a broker takes those documents and decides what to do with them. With HipoTech, every qualifying bank sees your full file simultaneously.',
    brokerLink: 'Why not a broker? Read the full comparison →',
  },
  whatToExpect: {
    title: 'What Foreign Buyers Should Expect',
    subtitle: 'Buying property in the DR as a foreigner is straightforward — but the mortgage process has specific requirements. Here\'s what to plan for.',
    downPayment: 'Down Payment',
    downPaymentRange: '10–40%',
    downPaymentDesc: 'Varies by bank and residency status. Foreign residents can qualify from 10% (Cibao, Santa Cruz) up to 30%. Non-residents from 10% (Cibao), 20% (Santa Cruz) up to 40%.',
    interestRates: 'Interest Rates (USD)',
    interestRatesRange: '8–10+%',
    interestRatesDesc: 'USD-denominated mortgages. DOP rates are typically higher.',
    timeline: 'Processing Time',
    timelineRange: '2–6 weeks',
    timelineDesc: 'From application submission to bank decision. HipoTech\'s multi-bank approach cuts this significantly.',
    docsTitle: 'Typical Documents Required',
    doc1: 'Valid passport and proof of residency status',
    doc2: 'Last 2 years of tax returns or income statements',
    doc3: 'Bank statements (last 3–6 months)',
    doc4: 'Proof of down payment funds',
    doc5: 'Property purchase agreement or listing (if applicable)',
    doc6: 'Employment verification letter',
  },
  comparison: {
    title: 'Traditional Process vs. HipoTech',
    subtitle: 'See how applying through HipoTech compares to the traditional bank-by-bank approach.',
    feature: 'Feature',
    traditional: 'Traditional Way',
    hipotech: 'HipoTech',
    row1feature: 'Applications',
    row1traditional: 'Visit each bank separately, fill out different forms',
    row1hipotech: 'One form, automatically sent to all qualifying banks',
    row2feature: 'Language',
    row2traditional: 'You translate or hire a translator',
    row2hipotech: 'Apply in English, French, or Spanish — we translate',
    row3feature: 'Account / Routing Numbers',
    row3traditional: 'Stored indefinitely in each bank\'s system',
    row3hipotech: 'Processed in memory, destroyed in 30 seconds — never stored',
    row4feature: 'Processing Time',
    row4traditional: '30–90 days, sequential per bank',
    row4hipotech: 'All banks review simultaneously — 2 to 6 weeks',
    row5feature: 'Tracking',
    row5traditional: 'Call each bank, wait for callbacks',
    row5hipotech: 'Real-time dashboard: delivered, opened, downloaded, responded',
    row6feature: 'Cost',
    row6traditional: '$1,500+ in fees, translation, and travel',
    row6hipotech: 'One processing fee — everything included',
  },
  autoExpiry: {
    title: 'Automatic Document Expiry',
    description: 'Your encrypted application documents automatically self-destruct after 30 days — because your sensitive information shouldn\'t exist longer than necessary. If needed, applications can be reprocessed.',
  },
  blogSlides: {
    badge: 'BLOG',
    readBlog: 'Read the entire blog',
    interestRates: {
      title: 'Why Are DR Interest Rates So High?',
      teaser: 'A deeper look reveals the story is more complex than it first appears.',
      readLink: 'Read: Why Are DR Interest Rates So High?',
    },
    appreciation: {
      title: 'Rates Are High — But So Are Returns',
      teaser: 'What every North American buyer should know before walking away from a DR mortgage.',
      readLink: 'Read: Rates Are High — But So Are Returns',
    },
    bankCapital: {
      title: 'Do Banks Have the Capital?',
      teaser: 'The DR mortgage market is one of the least saturated in the Western Hemisphere.',
      readLink: 'Read: Do Dominican Banks Have the Capital?',
    },
    dailyRentals: {
      title: 'Buy a Property, Rent It Daily',
      teaser: '11.6 million visitors and no STR restrictions — the investor\'s guide to short-term rentals.',
      readLink: 'Read: STR & CONFOTUR Investor Guide',
    },
  },
  promoBanner: {
    badge: 'Spring 2026 Offer',
    message: 'Submit your application this spring — if no bank issues a letter of intent, we refund you in full. Offer ends May 1.',
    cta: 'Start Now',
    dismiss: 'Dismiss',
  },
  pricing: {
    badge: 'TRANSPARENT PRICING',
    title: 'One Fee. Everything Included.',
    subtitle: 'No hidden costs, no recurring subscriptions. Pay once when you\'re ready to submit — creating your account and application is free.',
    oneTimePayment: 'One-time payment',
    perApplication: 'per application',
    whatsIncluded: 'Everything included:',
    feature1: 'Distribution to all qualifying banks',
    feature2: 'Automated Spanish translation',
    feature3: 'AI-powered bank matching',
    feature4: 'Document encryption & verification',
    feature5: 'Real-time tracking dashboard',
    feature6: 'Dedicated support team',
    startFree: 'Start Free — Pay When You Submit',
    noCreditCard: 'No credit card required to create your account',
    seeDetails: 'See full pricing details',
    traditionalCost: 'Bank by bank:',
    traditionalAmount: '$1,500+ in fees, translation, and travel',
    brokerCost: 'Via a broker:',
    brokerAmount: '$2,000–4,000+ in commissions (1–2% of loan)',
  },
  faq: {
    categories: {
      general: 'General Information',
      security: 'Security & Privacy',
      eligibility: 'Eligibility',
      process: 'Application Process',
      pricing: 'Pricing & Payments',
      technical: 'Technical Support',
      documents: 'Documents & Upload',
    },
    questions: {
      'general-1': {
        title: 'What makes HipoTech different from traditional mortgage brokers?',
        content: {
          intro: 'HipoTech combines cutting-edge technology with mortgage expertise:',
          items: [
            { label: 'Zero-Persistence Security:', text: 'We never store your most sensitive financial data (account numbers, routing numbers)' },
            { label: 'Automated Distribution:', text: 'One application reaches multiple banks instantly' },
            { label: 'Multi-Language Support:', text: 'Complete your application in any language' },
            { label: 'Real-Time Tracking:', text: 'Monitor your application status 24/7' },
            { label: 'AI-Powered Matching:', text: 'Smart bank recommendations based on your profile' },
          ],
        },
      },
      'general-2': {
        title: 'What languages are supported?',
        content: {
          intro: 'HipoTech currently supports applications in:',
          items: [
            { label: 'English:', text: 'Full application and support' },
            { label: 'Spanish:', text: 'Complete translation for all documents and communications' },
            { label: 'French:', text: 'Full application support and bank submissions' },
          ],
          footer: 'All bank submissions are automatically translated to Spanish for Dominican Republic banks.',
        },
      },
      'general-3': {
        title: 'What products does HipoTech offer?',
        content: {
          intro: 'HipoTech offers three main mortgage products tailored to different stages of your home buying journey:',
          items: [
            { label: 'Personal Pre-Qualification:', text: 'Get pre-qualified before finding a property to know your borrowing capacity' },
            { label: 'Property Pre-Qualification:', text: 'Fast pre-qualification for a specific property you\'ve already found' },
            { label: 'Full Mortgage Application:', text: 'Complete application with full underwriting and closing' },
          ],
          footer: 'Each product is designed for a specific need, from early planning to final purchase.',
        },
      },
      'general-4': {
        title: 'Why would I choose a Personal Pre-Qualification?',
      },
      'general-5': {
        title: 'Why would I choose a Property Pre-Qualification?',
      },
    },
  },
  simulator: {
    badge: 'Free tool — no account needed',
    ctaTitle: 'Find out how many banks you qualify with',
    ctaSubtitle: 'Enter your profile in 2 minutes. See eligibility, required documents, and your effective credit score — anonymously.',
    noSignup: 'No sign-up · No commitment · 2 minutes',
  },

  // ── Hardcoded strings (auto-generated) ──
  hardcoded: {
    seeItInAction: 'See It In Action',
    everyScreenEveryStep: 'Every screen, every step — no shortcuts, no staged demo. This is the real thing.',
    seeExactlyWhatYouGet: 'See exactly what you get before committing. No surprises, no hidden steps.',
    theFullEndToEnd: 'The full end-to-end process from account creation to bank submission.',
    seeHowToAcceptA: 'See how to accept a co-applicant invitation and get started from the email link.',
    enterYourPersonalFinancial: 'Enter your personal, financial and credit information independently — your data stays private.',
    saveProgressComeBack: 'Save progress, come back later, and upload documents whenever you are ready.',
    readWhyAreDrInterest: 'Read: Why Are DR Interest Rates So High?',
    readRatesAreHigh: 'Read: Rates Are High — But So Are Returns',
    readDoDominicanBanks: 'Read: Do Dominican Banks Have the Capital to Lend?',
    readBuyPropertyInDr: 'Read: Buy Property in DR, Rent It Daily — STR & CONFOTUR Guide',
      altFastBankResponse: 'HipoTech DR mortgage platform — fast bank response time',
      altMultiBankSubmission: 'HipoTech submits your Dominican Republic mortgage application to multiple banks simultaneously',
      altZeroPersistence: 'HipoTech zero-persistence security — sensitive mortgage data is never stored',
      altTransparentPricing: 'HipoTech transparent pricing — one fee covers your entire DR mortgage application',
  },

  blog: {
    sectionTitle: 'Resources for Foreign Buyers',
    sectionSubtitle: 'Everything you need to know about getting a mortgage in the Dominican Republic.',
    viewAll: 'View all articles',
    readMore: 'Read more',
    post1Title: 'How to Get a Mortgage in the DR as a Foreigner',
    post2Title: 'Can a US Citizen Get a Mortgage in the Dominican Republic?',
    post3Title: 'Can a Non-Resident Buy Property in the DR with a Mortgage?',
    post4Title: 'What Is the Minimum Down Payment for a Mortgage in the DR?',
    post5Title: 'What Fees Should I Expect When Getting a Mortgage in the DR?',
    post6Title: 'What Documents Do I Need to Apply for a Mortgage in the DR?',
  },

} as const;